import { useLocation } from 'react-router-dom';
import AppPaths from '../AppPaths';
import { useSelector } from 'react-redux';
import { AppState } from '../types';
import { showNanoVariant } from '../components/NanoBanner/NanoBanner';
import { showRescueGwaVariant } from '../views/RescueLanding/RescueBanner';
import { useSeries3Context } from '../contexts/Series3Context';
import { SUPPLEMENT_PREFIXES } from '../views/ManageSubscription/Supplements/SplashPage';

interface PathBasedAppCustomization {
  hideNav: boolean;
  inCheckoutFlow: boolean;
  showPromotionBanner: boolean;
  inBag: boolean;
  inShop: boolean;
}

export function usePathBasedAppCustomization(): PathBasedAppCustomization {
  const { pathname, search } = useLocation();
  const showPromoBannerImprovements = useSelector(
    (state: AppState) => !!state.config.siteConfig.enableEcommPromoBannerImprovements,
  );
  const disablePromoInCart = useSeries3Context().disablePromoBarInCart;

  const hideNav = pathname.startsWith('/series2upgrade') || pathname.startsWith('/purchasesubscription');
  const inCheckoutFlow = pathname.startsWith('/checkout') && !pathname.endsWith('/thank-you');
  const inBag = pathname === AppPaths.Bag;
  const inShop = pathname === AppPaths.Shop;
  const forceNanoBanner = showNanoVariant();
  const cart = useSelector((state: AppState) => state.storeShop.cart);
  const onlySupplementsInCart = Object.values(cart.cartItems).every((item) =>
    SUPPLEMENT_PREFIXES.some((prefix) => item.lineItem.sku.toUpperCase().startsWith(prefix)),
  );

  // Hide the generic `ShopPromotionBanner` whenever the URL carries a partner
  // `?pc=` code. `useAutoApplyCoupon` already owns coupon application for the
  // partner flow, and the promo banner's parallel `applyCouponToCart` call
  // falls through to Recurly's standalone JS SDK lookup, which returns
  // `not-found` for partner-scoped codes and surfaces a misleading
  // `api-error: Could not find the requested .` banner.
  const hasPartnerCodeParam = !!new URLSearchParams(search).get('pc')?.trim();

  // Show the promotion banner on product pages, PLP, the pick-your-fi shop, compare page, cart page,
  // and checkout flow. `/shop` needs it because clicking the banner is what applies the code, and
  // the hardware-first cards quote the applied price.
  // Suppressed for rescue GWA users — the green RescueBanner already covers the promo slot. On the
  // eligibility gate, not the redesign one: that banner renders off the cookie in both arms of a
  // design experiment, so control would otherwise stack two banners in the same slot.
  const showPromotionBanner =
    !hasPartnerCodeParam &&
    !showRescueGwaVariant() &&
    (pathname.startsWith('/products/smart-collar-v') ||
      pathname.startsWith('/products/fi-mini') ||
      pathname.startsWith('/products/fi-ultra') ||
      pathname === AppPaths.ProductListingPage ||
      pathname === AppPaths.Shop ||
      pathname === AppPaths.Compare ||
      forceNanoBanner ||
      (pathname.startsWith('/bag') && !disablePromoInCart && !onlySupplementsInCart) ||
      (showPromoBannerImprovements && inCheckoutFlow && pathname !== AppPaths.CheckoutSupplements));

  return {
    hideNav,
    inCheckoutFlow,
    showPromotionBanner,
    inBag,
    inShop,
  };
}
